import React from 'react';
import { Link } from 'react-router-dom';
import { Phone } from 'lucide-react';
import { SITE } from '../config/media';
import { usePageContent } from '../lib/useContent';
import Brand from './Brand';

// Phase 2: Footer content is CMS-editable at page_slug='footer',
// section_slug='root'. Hard-coded SITE values stay as the fallback
// so a cold cache / CMS outage still renders a complete footer.
const FOOTER_FALLBACK = {
    root: {
        tagline: 'Manufacturer Direct \u2022 Since 1955',
        phone_display: SITE.phoneDisplay,
        phone_tel: SITE.phone,
        email: SITE.salesEmail,
        serving: SITE.serving,
        addresses: [
            { label: 'Mill', city: 'Chemainus', region: 'BC', address: '' },
            { label: 'Sales', city: 'Calgary', region: 'AB', address: '' },
        ],
        socials: [],
        copyright: '\u00a9 Centurion Matting Products \u2022 Manufacturer Direct since 1955 \u2022 Chemainus, BC + Calgary, AB',
    },
};

/* IA mirrors the rebuilt header - Products / Projects / Partnerships /
 * Company.  Some routes don't yet exist (they intentionally 404 until
 * the page ships).  "Locations" is intentionally rendered as inert
 * The Company column omits a Locations entry until a real page exists. */
const COLUMNS = [
    {
        title: 'Products',
        testId: 'products',
        links: [
            { to: '/access-mats', label: 'Access Mats' },
            { to: '/rig-mats', label: 'Rig Mats' },
            { to: '/crane-mats', label: 'Crane Mats' },
            { to: '/catalog', label: 'All Products' },
        ],
    },
    {
        title: 'Projects',
        testId: 'projects',
        links: [
            { to: '/projects', label: 'All Projects' },
            { to: '/projects/infrastructure', label: 'Infrastructure' },
            { to: '/projects/oil-gas-pipeline', label: 'Oil & Gas / Pipeline' },
            { to: '/projects/renewables', label: 'Wind & Renewables' },
        ],
    },
    {
        title: 'Partnerships',
        testId: 'partnerships',
        links: [
            { to: '/partnerships/indigenous', label: 'Indigenous' },
            { to: '/partnerships/procurement', label: 'Procurement' },
            { to: '/partnerships/sustainability', label: 'Sustainability' },
        ],
    },
    {
        title: 'Company',
        testId: 'company',
        links: [
            { to: '/about', label: 'About Us' },
            { to: '/contact', label: 'Contact' },
            { to: '/quote', label: 'Request a Quote' },
        ],
    },
];

export const Footer = () => {
    const content = usePageContent('footer', FOOTER_FALLBACK);
    const root = content.root || FOOTER_FALLBACK.root;
    return (
        <footer
            className="border-t border-brand-hairline bg-brand-base"
            data-testid="site-footer"
        >
            <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
                <div className="grid grid-cols-2 gap-10 md:grid-cols-3 lg:grid-cols-5">
                    {COLUMNS.map((col) => (
                        <div
                            key={col.title}
                            className="min-w-0"
                            data-testid={`footer-col-${col.testId}`}
                        >
                            <h4 className="font-display text-xs font-bold uppercase tracking-[0.18em] text-brand-gold">
                                {col.title}
                            </h4>
                            <span
                                aria-hidden="true"
                                className="mt-3 mb-5 block h-px w-8 bg-brand-gold"
                            />
                            <ul className="space-y-3">
                                {col.links.map((l, i) => (
                                    <li key={`${col.title}-${i}`}>
                                        <Link
                                            to={l.to}
                                            className="font-sans text-sm text-brand-muted transition-colors duration-150 hover:text-brand-text"
                                        >
                                            {l.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}

                    <div className="min-w-0" data-testid="footer-col-contact">
                        <h4 className="font-display text-xs font-bold uppercase tracking-[0.18em] text-brand-gold">
                            Contact
                        </h4>
                        <span
                            aria-hidden="true"
                            className="mt-3 mb-5 block h-px w-8 bg-brand-gold"
                        />
                        <ul className="space-y-3">
                            <li>
                                <a
                                    href={`tel:${root.phone_tel || SITE.phone}`}
                                    data-testid="footer-phone"
                                    className="inline-flex items-center gap-2 font-sans text-sm font-semibold tabular-nums text-brand-text transition-colors duration-150 hover:text-brand-gold"
                                >
                                    <Phone size={14} />
                                    {root.phone_display || SITE.phoneDisplay}
                                </a>
                            </li>
                            <li>
                                <a
                                    href={`mailto:${root.email || SITE.salesEmail}`}
                                    data-testid="footer-email"
                                    className="block break-all font-sans text-sm text-brand-muted transition-colors duration-150 hover:text-brand-text"
                                >
                                    {root.email || SITE.salesEmail}
                                </a>
                            </li>
                            {(root.addresses || []).filter((a) => a.city).map((a, i) => (
                                <li key={`${a.city}-${i}`} className="font-sans text-sm text-brand-muted">
                                    {a.city}{a.region ? `, ${a.region}` : ''}
                                </li>
                            ))}
                            <li
                                className="pt-2 font-sans text-xs leading-relaxed text-brand-muted/80"
                                data-testid="footer-serving-line"
                            >
                                {root.serving || SITE.serving}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <div
                aria-hidden="true"
                className="mx-auto h-px max-w-7xl bg-brand-blue-soft"
                data-testid="footer-blue-hairline"
            />

            <div className="border-t border-brand-hairline">
                <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-6 py-6 text-xs text-brand-muted md:flex-row md:items-center md:px-10">
                    <div className="flex items-center gap-5">
                        <div className="flex flex-col gap-2">
                            <Brand height={40} interactive={false} testId="footer-brand" />
                            <span
                                data-testid="footer-tagline"
                                className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-brand-gold"
                            >
                                {root.tagline}
                            </span>
                        </div>
                        <p data-testid="footer-bottom-strip" className="font-sans tracking-wide">
                            {root.copyright}
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-4">
                        <Link
                            to="/login"
                            data-testid="footer-admin-link"
                            className="font-sans text-xs text-brand-muted transition-colors duration-150 hover:text-brand-blue hover:underline underline-offset-4"
                        >
                            Staff sign-in →
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
